import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import {
  MAX_LENGTH, getIbanMask
} from '../../../constants/constants'
import { InputMasked } from '../../../ui'
import { usePresetValue, usePrevious } from '../../utils/hooks'
import { getUserValue } from '../utils'

const ExternalIban = ({ i18nLabel, type, name, required }) => {
  const { t } = useTranslation()
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [isShown, setIsShown] = useState(true)
  const [{
    whitelabel: { extFields },
    form,
    form: { accId },
    user
  }, dispatch] = useStore()
  const { isSwiftOrIban: swiftOrIbanFldValue } = form[accId]
  const prevSwiftOrIbanFldValue = usePrevious(swiftOrIbanFldValue)
  const userValue = getUserValue(accId, user, name)

  useEffect(() => {
    if (swiftOrIbanFldValue && prevSwiftOrIbanFldValue === swiftOrIbanFldValue) return
    const swiftOrIbanFld = extFields.find(fld => fld.name === 'isSwiftOrIban')
    if (!swiftOrIbanFld) return

    if (swiftOrIbanFld && !swiftOrIbanFldValue) {
      setIsShown(false)
      dispatch({ type: 'removeValidationFld', name })
      return
    }

    const isShown = swiftOrIbanFldValue === name
    setIsShown(isShown)
    if (isShown) {
      dispatch({ type: 'addValidationFld', name })
      if (value.length) {
        dispatch({ type: 'setExtFieldValue', name, value })
        dispatch({ type: 'setValidationFld', name, value: isValid })
      }
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
    }
  }, [swiftOrIbanFldValue, value, isValid])

  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(value)
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  const mask = useMemo(() => getIbanMask(), [name])

  const onChange = ({ target: { value } }) => {
    setValue(value)
  }

  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    let isValid
    if (!required) {
      isValid = !trimmedValue.length || (trimmedValue.length && getIsValid(trimmedValue))
    } else {
      isValid = getIsValid(trimmedValue)
    }

    if (isValid) {
      setIsValid(true)
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  const getIsValid = (value) => value.length > 0

  return isShown ? (
    <InputMasked
      name={name}
      label={label}
      value={value}
      type={type}
      mask={mask}
      guide={false}
      key={name}
      maxLength={MAX_LENGTH}
      error=''
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
    />
  ) : null
}

const getExternalIbanFields = textField => {
  const { i18n, name, required, type, order } = textField
  return (
    <ExternalIban
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
    />
  )
}

export { ExternalIban, getExternalIbanFields }
